import type { ComponentChildren } from "preact";
import { useCallback, useLayoutEffect, useRef, useState } from "preact/hooks";
import { formatNumber } from "../lib/format.ts";
import { Link } from "./Button.tsx";
import { Chevron } from "./Chevron.tsx";
import { Chip } from "./Chip.tsx";
import { Gate, useHold } from "./gate.ts";

/** Lists this short start open; longer ones start closed, as a tree's groups do. */
const OPEN_UP_TO = 3;

/**
 * A list that folds, as a tree's group does: its name and how many, opened and closed by its chevron or its name,
 * what's in it drawn only while it's open (fetched then). Short lists (`count` up to [`OPEN_UP_TO`]) start open.
 * With `href`, its name is a link there. `extra`: beside its name (a view toggle, a link to it as a list), there whether it's open or not.
 */
export function Fold(p: {
  /** Said by the chevron (what opens or closes). */
  name: string;
  /** Shown as its name (the plain name when absent). */
  label?: ComponentChildren;
  count: number;
  extra?: ComponentChildren;
  /** Open at first whatever its count. */
  open?: boolean | undefined;
  /** Inside another fold: its name on a band of its own, so it reads as part of the other (nothing indented). */
  nested?: boolean;
  /** Told as it opens or closes (to open it as it was when it's drawn again). */
  onToggle?: (open: boolean) => void;
  /** Under its name (a search adding to it). */
  under?: ComponentChildren;
  /** Its `extra` and `under` only while it's open (tools for what's in it). */
  toolsWhenOpen?: boolean;
  /** Where its name leads (its items as a list of their own): then only its chevron opens and closes it. */
  href?: string;
  children: () => ComponentChildren;
}) {
  const [open, setOpen] = useState(p.open ?? p.count <= OPEN_UP_TO);
  // Opened, what's in it shows once all of it has loaded (no list growing as each part comes); later loads (more
  // asked for) show as they come. What's loading is kept as it's told (a ref: its parts tell it before it looks).
  const loading = useRef(new Set<object>());
  const [changed, setChanged] = useState(0);
  const [shown, setShown] = useState(false);
  const hold = useCallback((key: object, pending: boolean) => {
    if (loading.current.has(key) !== pending) {
      if (pending) {
        loading.current.add(key);
      } else {
        loading.current.delete(key);
      }
      setChanged((n) => n + 1);
    }
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: looked at again as its parts tell it (`changed`).
  useLayoutEffect(() => {
    if (open && !shown && loading.current.size === 0) {
      setShown(true);
    }
  }, [open, shown, changed]);
  const waiting = open && !shown;
  // Inside another fold being opened, that one waits for this one's too.
  useHold(waiting);
  const toggle = () => {
    setOpen(!open);
    setShown(false);
    p.onToggle?.(!open);
  };
  return (
    <section aria-label={p.name} class="flex flex-col gap-2">
      {/* As tall as one with tools beside its name, nested or not. */}
      <div
        class={`flex min-h-7 flex-wrap items-center gap-x-2 gap-y-1 text-sm ${p.nested ? "rounded-md bg-zinc-950/[0.03] px-1 py-0.5" : ""}`}
      >
        <Chevron open={open} label={p.name} onToggle={toggle} />
        {p.href ? (
          <Link href={p.href} class="flex items-center gap-2 text-zinc-700 hover:underline">
            {p.label ?? <span class="font-medium text-zinc-900">{p.name}</span>}
          </Link>
        ) : (
          <button
            type="button"
            class="flex items-center gap-2 text-zinc-700 hover:underline"
            tabIndex={-1}
            onClick={toggle}
          >
            {p.label ?? <span class="font-medium text-zinc-900">{p.name}</span>}
          </button>
        )}
        <Chip>{formatNumber(p.count)}</Chip>
        {/* Kept in its place while closed, unseen: the row the same height open or closed. */}
        {open || !p.toolsWhenOpen ? p.extra : <span class="invisible contents">{p.extra}</span>}
      </div>
      {open || !p.toolsWhenOpen ? p.under : null}
      {open ? (
        <Gate.Provider value={hold}>
          <div class="min-w-0" hidden={waiting}>
            {p.children()}
          </div>
        </Gate.Provider>
      ) : null}
    </section>
  );
}
